import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { LanguageService, LanguageCode } from '../i18n/language';

/**
 * Справочники в БД пока хранят только name_ru/name_en (нет отдельных
 * казахского и китайского названий) — до тех пор используем ту же схему
 * фолбэка, что и для остального интерфейса: kk → ru, zh → en.
 */
function pickDictionaryName(nameRu: string, nameEn: string, lang: LanguageCode): string {
  return lang === 'en' || lang === 'zh' ? nameEn : nameRu;
}

export interface CountryDto {
  id: number;
  nameRu: string;
  nameEn: string;
  code: string;
  phoneIndex: string;
  continent: string;
  centralAsia: boolean;
  // Computed property для получения имени в зависимости от языка
  name?: string;
}

export interface IndustryDto {
  id: number;
  nameRu: string;
  nameEn: string;
  // Computed property для получения имени в зависимости от языка
  name?: string;
}

export interface IncotermDto {
  id: number;
  code: string;
  nameRu: string;
  nameEn: string;
  // Computed property для получения имени в зависимости от языка
  name?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DictionaryService {
  private apiUrl = '/cargo/open-api/dic';

  constructor(
    private http: HttpClient,
    private languageService: LanguageService
  ) {}

  private getCountryName(country: CountryDto, lang: LanguageCode): string {
    return pickDictionaryName(country.nameRu, country.nameEn, lang);
  }

  private getIndustryName(industry: IndustryDto, lang: LanguageCode): string {
    return pickDictionaryName(industry.nameRu, industry.nameEn, lang);
  }

  getAllCountries(): Observable<CountryDto[]> {
    return this.http.get<CountryDto[]>(`${this.apiUrl}/countries`).pipe(
      map(countries => {
        const currentLang = this.languageService.getCurrentLanguageValue();
        return countries.map(country => ({
          ...country,
          name: this.getCountryName(country, currentLang)
        })).sort((a, b) => {
          const nameA = this.getCountryName(a, currentLang);
          const nameB = this.getCountryName(b, currentLang);
          return nameA.localeCompare(nameB);
        });
      })
    );
  }

  getAllIndustries(): Observable<IndustryDto[]> {
    return this.http.get<IndustryDto[]>(`${this.apiUrl}/industries`).pipe(
      map(industries => {
        const currentLang = this.languageService.getCurrentLanguageValue();
        return industries.map(industry => ({
          ...industry,
          name: this.getIndustryName(industry, currentLang)
        })).sort((a, b) => {
          const nameA = this.getIndustryName(a, currentLang);
          const nameB = this.getIndustryName(b, currentLang);
          return nameA.localeCompare(nameB);
        });
      })
    );
  }

  getCargoTypes(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/cargo-types`);
  }

  getIncoterms(): Observable<IncotermDto[]> {
    return this.http.get<IncotermDto[]>(`${this.apiUrl}/incoterms`).pipe(
      map(incoterms => {
        const currentLang = this.languageService.getCurrentLanguageValue();
        return incoterms.map(incoterm => ({
          ...incoterm,
          name: pickDictionaryName(incoterm.nameRu, incoterm.nameEn, currentLang)
        }));
      })
    );
  }

  getCurrencies(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/currencies`);
  }

  getRoles(): Observable<RoleDto[]> {
    return this.http.get<RoleDto[]>(`${this.apiUrl}/roles`);
  }

  getAllFreightForwarderServices(): Observable<FreightForwarderServiceDto[]> {
    return this.http.get<FreightForwarderServiceDto[]>(`${this.apiUrl}/freight-forwarder-services`).pipe(
      map(services => {
        const currentLang = this.languageService.getCurrentLanguageValue();
        return services.map(service => ({
          ...service,
          name: pickDictionaryName(service.nameRu, service.nameEn, currentLang)
        }));
      })
    );
  }

  getAllDealActivityTypes(): Observable<DealActivityTypeDto[]> {
    return this.http.get<DealActivityTypeDto[]>(`${this.apiUrl}/deal-activity-types`).pipe(
      map(types => {
        const currentLang = this.languageService.getCurrentLanguageValue();
        return types.map(type => ({
          ...type,
          name: pickDictionaryName(type.nameRu, type.nameEn, currentLang)
        }));
      })
    );
  }
}

export interface RoleDto {
  id: number;
  name: string;
  code: string;
}

export interface FreightForwarderServiceDto {
  id: number;
  nameRu: string;
  nameEn: string;
  name?: string;
}

export interface DealActivityTypeDto {
  id: number;
  code: string;
  nameRu: string;
  nameEn: string;
  category?: 'DOCUMENT' | 'STATUS' | 'REQUEST';
  name?: string;
}
